import {hasMarker} from "@cm/template-nodes"
import type {EvaluableTemplateNode, IGetInfoNodeParts} from "@cm/template-nodes"
import type {MattressCurveData} from "#mattress/nodes/curve/mattress-curve"
import type {MattressComponentGraphData} from "#mattress/nodes/component/mattress-component-types"
import type {MattressReferenceFrameData} from "#mattress/nodes/reference-frame/mattress-reference-frame"
import type {NodeParameters} from "@cm/graph-v3"

import type {Vector2Like} from "@cm/math"

// Marker-based "like" sets for mattress-domain nodes, following the v2 role-marker convention
// (see ObjectLikeNode/MeshLikeNode). Each concrete mattress curve/ref-frame/component-graph
// declares `[markerSymbol] = true`; the z.custom validators in validation.ts use the `is*` predicates.

export const mattressCurveLikeMarker = Symbol("MattressCurveLikeNode")
export const mattressReferenceFrameLikeMarker = Symbol("MattressReferenceFrameLikeNode")
export const mattressComponentGraphLikeMarker = Symbol("MattressComponentGraphLikeNode")

export interface IMattressReferenceFrame {
    toGlobalUVSpace(uv: Vector2Like, context: IGetInfoNodeParts): Vector2Like
    fromGlobalUVSpace(uv: Vector2Like, context: IGetInfoNodeParts): Vector2Like
}

// All mattress-like nodes carry a `parameters.name` — surface it here so consumers iterating
// marker-typed nodes get a typed `name` without per-class narrowing.
export interface MattressCurveLikeNode extends EvaluableTemplateNode<MattressCurveData | null> {
    readonly [mattressCurveLikeMarker]: true
    readonly parameters: NodeParameters & {readonly name: string}
}
export interface MattressReferenceFrameLikeNode extends EvaluableTemplateNode<MattressReferenceFrameData | null>, IMattressReferenceFrame {
    readonly [mattressReferenceFrameLikeMarker]: true
    readonly parameters: NodeParameters & {readonly name: string}
}
export interface MattressComponentGraphLikeNode extends EvaluableTemplateNode<MattressComponentGraphData | null> {
    readonly [mattressComponentGraphLikeMarker]: true
    readonly parameters: NodeParameters & {readonly name: string}
}

export const isMattressCurveLikeNode = (x: unknown): x is MattressCurveLikeNode => hasMarker(x, mattressCurveLikeMarker)
export const isMattressReferenceFrameLikeNode = (x: unknown): x is MattressReferenceFrameLikeNode => hasMarker(x, mattressReferenceFrameLikeMarker)
export const isMattressComponentGraphLikeNode = (x: unknown): x is MattressComponentGraphLikeNode => hasMarker(x, mattressComponentGraphLikeMarker)
